import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Button } from 'reactstrap';
import AiTranscript from './AiTranscript';

/**
 * AiAssistantPanel — chat panel root component.
 *
 * Props:
 *   transcript      {Array}    - current conversation entries
 *   isPending       {boolean}  - request in flight
 *   onSend          {Function} - (instruction: string) => void
 *   onUndo          {Function} - (entryId: string) => void
 *   onClose         {Function} - close/collapse the panel
 */
class AiAssistantPanel extends Component {
    constructor(props) {
        super(props);
        this.state = {
            inputValue: '',
        };
        this._textareaRef = React.createRef();
    }

    _handleInputChange = (e) => {
        this.setState({ inputValue: e.target.value });
    };

    _handleSend = () => {
        const { inputValue } = this.state;
        const { onSend, isPending } = this.props;
        const trimmed = inputValue.trim();
        if (!trimmed || isPending) return;
        onSend(trimmed);
        this.setState({ inputValue: '' });
    };

    _handleKeyDown = (e) => {
        // Ctrl+Enter or Cmd+Enter submits
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            this._handleSend();
        }
    };

    _handleExampleClick = (text) => {
        this.setState({ inputValue: text }, () => {
            if (this._textareaRef.current) {
                this._textareaRef.current.focus();
            }
        });
    };

    render() {
        const { transcript, isPending, onUndo, onClose, intl } = this.props;
        const { inputValue } = this.state;

        const canSend = inputValue.trim().length > 0 && !isPending;

        return (
            <div
                id="ai-assistant-panel"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    backgroundColor: '#fff',
                }}
            >
                {/* ── Panel header ─────────────────────────────────────────── */}
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        borderBottom: '1px solid #dee2e6',
                        flexShrink: 0,
                        backgroundColor: '#f8f9fa',
                    }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <i
                            className="fa-solid fa-robot"
                            aria-hidden="true"
                            style={{ color: '#6f42c1', fontSize: '0.9rem' }}
                        />
                        <span style={{ fontWeight: 600, fontSize: '0.875rem', color: '#212529' }}>
                            <FormattedMessage id="TemplateAssistant.Panel.Title" />
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={intl.formatMessage({ id: 'TemplateAssistant.Panel.Close' })}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: '2px 4px',
                            cursor: 'pointer',
                            color: '#6c757d',
                            lineHeight: 1,
                            fontSize: '1rem',
                        }}
                    >
                        <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                </div>

                {/* ── Transcript ───────────────────────────────────────────── */}
                <AiTranscript
                    transcript={transcript}
                    isPending={isPending}
                    onUndo={onUndo}
                    onExampleClick={this._handleExampleClick}
                    intl={intl}
                />

                {/* ── Input area ───────────────────────────────────────────── */}
                <div
                    style={{
                        padding: '8px 12px',
                        borderTop: '1px solid #dee2e6',
                        flexShrink: 0,
                        backgroundColor: '#f8f9fa',
                    }}
                >
                    <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                        <FormattedMessage id="TemplateAssistant.Input.Placeholder">
                            {placeholder => (
                                <textarea
                                    ref={this._textareaRef}
                                    value={inputValue}
                                    onChange={this._handleInputChange}
                                    onKeyDown={this._handleKeyDown}
                                    placeholder={placeholder}
                                    disabled={isPending}
                                    aria-label={placeholder}
                                    rows={2}
                                    style={{
                                        flexGrow: 1,
                                        resize: 'none',
                                        borderRadius: 6,
                                        border: '1px solid #ced4da',
                                        padding: '6px 8px',
                                        fontSize: '0.85rem',
                                        fontFamily: 'inherit',
                                        lineHeight: 1.4,
                                        outline: 'none',
                                        backgroundColor: isPending ? '#f8f9fa' : '#fff',
                                        color: '#212529',
                                        boxSizing: 'border-box',
                                    }}
                                />
                            )}
                        </FormattedMessage>

                        <Button
                            size="sm"
                            className="btn-host"
                            onClick={this._handleSend}
                            disabled={!canSend}
                            aria-disabled={!canSend}
                            title={intl.formatMessage({
                                id: isPending
                                    ? 'TemplateAssistant.Input.Sending'
                                    : 'TemplateAssistant.Input.Send',
                            })}
                            style={{ flexShrink: 0, alignSelf: 'flex-end' }}
                        >
                            {isPending
                                ? <i className="fa-solid fa-circle-notch fa-spin" aria-hidden="true" />
                                : <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                            }
                            {' '}
                            <FormattedMessage
                                id={isPending
                                    ? 'TemplateAssistant.Input.Sending'
                                    : 'TemplateAssistant.Input.Send'
                                }
                            />
                        </Button>
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#6c757d', marginTop: 3 }}>
                        <FormattedMessage id="TemplateAssistant.Input.CtrlEnterHint" />
                    </div>
                </div>
            </div>
        );
    }
}

export default injectIntl(AiAssistantPanel);
